import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Shield, ChevronRight } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
};

// Helper to render text with **bold** markers
const renderTextWithBold = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const sections = [
  {
    id: 'introduction',
    title: '1. Introduction',
    content: `Welcome to Ulearning ("Company," "we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, disclose, and safeguard your information when you use our learning platform and services.

By accessing or using Ulearning, you agree to this privacy policy. If you do not agree with the terms of this privacy policy, please do not access the platform.`,
  },
  {
    id: 'information-collected',
    title: '2. Information We Collect',
    content: `We collect information that you provide directly to us, information we obtain automatically when you use our platform, and information from third-party sources.

**Personal Information You Provide:**
• Account Information: Name, email address, password, profile picture, and account preferences
• Payment Information: Billing address, payment card details (processed securely by our payment provider Stripe)
• Profile Information: Biography, professional background, skills, and learning preferences
• Course Content: Content you create if you're an instructor, including videos, documents, and assessments
• Communications: Messages you send to instructors, other students, or our support team

**Information Collected Automatically:**
• Device Information: IP address, browser type, operating system, device identifiers
• Usage Data: Pages visited, features used, courses viewed, time spent on platform
• Cookies and Tracking: Session cookies, preference cookies, and analytics cookies
• Log Data: Server logs including access times, error reports, and referral URLs`,
  },
  {
    id: 'use-of-information',
    title: '3. How We Use Your Information',
    content: `We use the information we collect for various purposes, including:

**Service Delivery:**
• Creating and managing your account
• Processing payments and course enrollments
• Providing access to courses and learning materials
• Issuing certificates upon course completion
• Facilitating communication between students and instructors

**Platform Improvement:**
• Analyzing usage patterns to improve our platform
• Developing new features and services
• Conducting research and analytics
• Troubleshooting technical issues

**Communications:**
• Sending transactional emails (purchase confirmations, password resets)
• Providing customer support
• Sending marketing communications (with your consent)
• Notifying you of updates to our terms or policies

**Legal and Security:**
• Complying with legal obligations
• Enforcing our terms of service
• Detecting and preventing fraud
• Protecting the security of our platform and users`,
  },
  {
    id: 'information-sharing',
    title: '4. How We Share Your Information',
    content: `We do not sell your personal information. We may share your information in the following circumstances:

**With Your Consent:** When you explicitly agree to share your information.

**Service Providers:** We share information with trusted third-party service providers who assist us in operating our platform:
• Stripe for payment processing
• Firebase for authentication and infrastructure
• Cloud hosting providers for data storage
• Analytics providers for platform insights
• Email service providers for communications

**Instructors:** When you enroll in a course, the instructor may see your name, profile information, and course progress.

**Legal Requirements:** We may disclose information if required by law, court order, or government request.

**Business Transfers:** In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.

**Protection of Rights:** We may share information to protect our rights, privacy, safety, or property, or that of our users or others.`,
  },
  {
    id: 'data-security',
    title: '5. Data Security',
    content: `We implement appropriate technical and organizational security measures to protect your personal information, including:

• Encryption of data in transit using TLS/SSL
• Encryption of sensitive data at rest
• Secure authentication using Firebase Authentication
• Regular security assessments and penetration testing
• Access controls limiting employee access to personal data
• Secure payment processing through PCI-DSS compliant providers

While we strive to protect your information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.`,
  },
  {
    id: 'data-retention',
    title: '6. Data Retention',
    content: `We retain your personal information for as long as necessary to:

• Provide our services to you
• Comply with legal obligations
• Resolve disputes and enforce agreements
• Maintain business records

When you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain it for legal or legitimate business purposes.

Course completion records and certificates may be retained indefinitely for verification purposes.`,
  },
  {
    id: 'your-rights',
    title: '7. Your Rights',
    content: `Depending on your location, you may have certain rights regarding your personal information:

**Access:** You can request a copy of your personal data.

**Correction:** You can request correction of inaccurate or incomplete data.

**Deletion:** You can request deletion of your personal data (subject to certain exceptions).

**Portability:** You can request your data in a portable format.

**Opt-out:** You can opt out of marketing communications at any time.

**Withdraw Consent:** Where processing is based on consent, you can withdraw it at any time.

To exercise these rights, please contact us at privacy@ulearning.com or through your account settings.`,
  },
  {
    id: 'cookies',
    title: '8. Cookies and Tracking Technologies',
    content: `We use cookies and similar technologies to enhance your experience:

**Essential Cookies:** Required for platform functionality (authentication, security).

**Preference Cookies:** Remember your settings and preferences.

**Analytics Cookies:** Help us understand how users interact with our platform. We use PostHog for product analytics.

**Marketing Cookies:** Used to deliver relevant advertisements (with your consent).

You can control cookies through your browser settings. Disabling essential cookies may affect platform functionality.`,
  },
  {
    id: 'session-recordings',
    title: '9. Session Recordings',
    content: `With your consent, we may record your sessions to improve our platform. These recordings help us understand user experience issues and improve functionality.

**What is recorded:**
• Page views and navigation patterns
• Button clicks and interactions
• Session duration and general usage patterns

**What is NOT recorded (automatically masked):**
• Passwords and authentication credentials
• Email addresses and personal contact information
• Payment information and credit card details
• Text entered in form fields
• Personal messages and private content

**Your control:**
• Session recordings only occur after you explicitly consent
• You can disable recordings anytime in Settings → Privacy
• Recordings are stored securely and deleted after 30 days
• Only authorized team members can access recordings for support purposes

We use PostHog, a privacy-focused analytics platform, for session recordings. PostHog is GDPR compliant and data is processed securely.`,
  },
  {
    id: 'children',
    title: '10. Children\'s Privacy',
    content: `Ulearning is not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.

If we discover that we have collected personal information from a child under 16, we will delete it promptly.`,
  },
  {
    id: 'international',
    title: '11. International Data Transfers',
    content: `Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws.

We ensure appropriate safeguards are in place for international transfers, including:
• Standard contractual clauses approved by relevant authorities
• Data processing agreements with service providers
• Compliance with applicable data transfer frameworks

By using our platform, you consent to the transfer of your information to the United States and other countries where we operate.`,
  },
  {
    id: 'changes',
    title: '12. Changes to This Policy',
    content: `We may update this privacy policy from time to time. We will notify you of material changes by:
• Posting the updated policy on our platform
• Sending you an email notification
• Displaying a prominent notice on our website

We encourage you to review this policy periodically. Your continued use of Ulearning after changes become effective constitutes acceptance of the revised policy.`,
  },
  {
    id: 'contact',
    title: '13. Contact Us',
    content: `If you have questions about this privacy policy or our data practices, please contact us:

**Ulearning**
Columbus, Ohio, United States

**Email:** privacy@ulearning.com
**General Support:** support@ulearning.com

For data protection inquiries in the EU, you may also contact our Data Protection Officer at dpo@ulearning.com.`,
  },
];

export default function PrivacyPolicyPage() {
  const lastUpdated = 'January 7, 2026';

  return (
    <div className="bg-void min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-slate/15 rounded-full blur-[120px]" />
        </div>

        <div className="container-luxury relative">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="max-w-3xl mx-auto"
          >
            <div className="mb-6 flex items-center gap-2 text-white/40 text-sm">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Privacy Policy</span>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <div className="icon-slate">
                <Shield className="w-5 h-5 text-slate-light" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white">
                Privacy Policy
              </h1>
            </div>

            <p className="text-white/50">
              Last updated: {lastUpdated}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="pb-10">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card"
            >
              <h2 className="font-semibold text-white mb-4">Table of Contents</h2>
              <nav className="grid sm:grid-cols-2 gap-2">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="text-white/60 hover:text-white text-sm transition-colors flex items-center gap-2"
                  >
                    <ChevronRight className="w-3 h-3" />
                    {section.title}
                  </a>
                ))}
              </nav>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto space-y-12">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ delay: index * 0.05 }}
                className="scroll-mt-24"
              >
                <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-white/10">
                  {section.title}
                </h2>
                <div className="text-white/60 leading-relaxed whitespace-pre-line prose-sm">
                  {section.content.split('\n').map((paragraph, i) => {
                    // Check if line is a header (starts and ends with **)
                    if (paragraph.startsWith('**') && paragraph.endsWith('**') && paragraph.indexOf('**', 2) === paragraph.length - 2) {
                      return (
                        <h3 key={i} className="font-semibold text-white mt-4 mb-2">
                          {paragraph.slice(2, -2)}
                        </h3>
                      );
                    }
                    // Check if line starts with ** (subheading like **Access:** text)
                    if (paragraph.startsWith('**')) {
                      return (
                        <p key={i} className="mb-2">
                          {renderTextWithBold(paragraph)}
                        </p>
                      );
                    }
                    if (paragraph.startsWith('•')) {
                      return (
                        <p key={i} className="ml-4 mb-1">
                          {renderTextWithBold(paragraph)}
                        </p>
                      );
                    }
                    return paragraph ? (
                      <p key={i} className="mb-3">
                        {renderTextWithBold(paragraph)}
                      </p>
                    ) : null;
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 border-t border-white/[0.06]">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-white/50 mb-4">
              Have questions about our privacy practices?
            </p>
            <Link to="/contact" className="btn-secondary">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

